/**
 * Wormhole Bridge Hook
 * Handles cross-chain WELEPHANT transfers using Portal Bridge Relayer
 */

import { useState, useCallback, useEffect } from 'react';
import { Signer, formatUnits } from 'ethers';
import { ChainConfig, BASE_CHAIN, BNB_CHAIN } from '@/lib/chains';
import {
  bridgeWelephant,
  getQuoteForDirection,
  estimateWelephantReceived,
  BridgeQuote as PortalQuote,
  BridgeDirection,
  WITH_REFERRER,
} from '@/lib/bridgeWelephant';

// Storage keys for bridge state persistence
const BRIDGE_PENDING_TX_KEY = 'welephant_bridge_pending_tx';

// WELEPHANT token decimals
const WELEPHANT_DECIMALS = 9;

// Export the spender address for approvals
export const BRIDGE_SPENDER_ADDRESS = WITH_REFERRER;

export interface BridgeQuote {
  sourceAmount: bigint;
  destAmount: bigint;
  relayerFee: bigint;
  relayerFeeFormatted: string;
  relayerFeeUsd: number;
  estimatedTime: string;
  nativeGasAmount?: bigint;
  routeName: string;
  isAutomatic: boolean;
  executionCost: string;
}

export interface BridgeTransfer {
  id: string;
  sourceChain: ChainConfig;
  destChain: ChainConfig;
  amount: bigint;
  txHash?: string;
  status: 'pending' | 'submitted' | 'relaying' | 'completed' | 'failed';
  error?: string;
  createdAt: number;
}

export interface WormholeBridgeState {
  isInitialized: boolean;
  isQuoting: boolean;
  isBridging: boolean;
  quote: BridgeQuote | null;
  pendingTransfer: BridgeTransfer | null;
  error: string | null;
  availableRoutes: string[];
}

// Serialization helpers for localStorage (BigInt can't be JSON stringified)
interface SerializedBridgeTransfer {
  id: string;
  sourceChain: ChainConfig;
  destChain: ChainConfig;
  amount: string;
  txHash?: string;
  status: 'pending' | 'submitted' | 'relaying' | 'completed' | 'failed';
  error?: string;
  createdAt: number;
}

function serializeTransfer(transfer: BridgeTransfer): string {
  const serialized: SerializedBridgeTransfer = {
    ...transfer,
    amount: transfer.amount.toString(),
  };
  return JSON.stringify(serialized);
}

function getDirection(sourceChainId: number): BridgeDirection {
  if (sourceChainId === BNB_CHAIN.id) {
    return 'BNB_TO_BASE';
  } else if (sourceChainId === BASE_CHAIN.id) {
    return 'BASE_TO_BNB';
  }
  throw new Error(`Unsupported chain id ${sourceChainId}`);
}

export function useWormholeBridge() {
  const [state, setState] = useState<WormholeBridgeState>({
    isInitialized: true, // No SDK initialization needed
    isQuoting: false,
    isBridging: false,
    quote: null,
    pendingTransfer: null,
    error: null,
    availableRoutes: ['PortalBridge'],
  });

  // Clear any stale pending transfers on mount
  // (History is now the source of truth for completed transfers)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(BRIDGE_PENDING_TX_KEY);
  }, []);

  // Get a quote for the bridge transfer
  // Note: requestNativeGas and destChain parameters are kept for API compatibility but not used
  const getQuote = useCallback(async (
    sourceChain: ChainConfig,
    _destChain: ChainConfig,
    amountIn: bigint,
    _requestNativeGas: boolean = false,
    signer?: Signer, // Optional signer for accurate estimation
  ): Promise<BridgeQuote | null> => {
    setState(prev => ({ ...prev, isQuoting: true, error: null }));

    try {
      const direction = getDirection(sourceChain.id);

      console.log('[WormholeBridge] Getting quote for:', {
        direction,
        amount: amountIn.toString(),
      });

      // Get quote from Portal Bridge API
      const portalQuote: PortalQuote = await getQuoteForDirection(direction);

      console.log('[WormholeBridge] Portal quote:', portalQuote);

      // The execution cost is in native token (wei)
      const executionCostWei = BigInt(portalQuote.estimatedCost);
      const executionCostFormatted = formatUnits(executionCostWei, 18);
      const nativeSymbol = sourceChain.id === BNB_CHAIN.id ? 'BNB' : 'ETH';

      // Calculate accurate destination amount if signer is provided
      let destAmount = amountIn; // Fallback to 1:1
      if (signer) {
        try {
          const amountFormatted = formatUnits(amountIn, WELEPHANT_DECIMALS);
          const estimation = await estimateWelephantReceived({
            signer,
            amount: amountFormatted,
            referrerDbps: 0, // No referrer fee configured
          });
          destAmount = BigInt(estimation.expectedDstWei);
          console.log('[WormholeBridge] Estimated destination amount:', {
            srcWei: estimation.amountSrcWei,
            dstWei: estimation.expectedDstWei,
            dstHuman: estimation.expectedDstHuman,
          });
        } catch (estErr) {
          console.warn('[WormholeBridge] Estimation failed, using 1:1:', estErr);
        }
      }

      const quote: BridgeQuote = {
        sourceAmount: amountIn,
        destAmount,
        relayerFee: 0n, // No token fee, only native gas
        relayerFeeFormatted: `${parseFloat(executionCostFormatted).toFixed(6)} ${nativeSymbol}`,
        relayerFeeUsd: 0,
        estimatedTime: '~10-15 minutes',
        nativeGasAmount: undefined, // Native gas drop not supported in this integration
        routeName: 'PortalBridge',
        isAutomatic: true,
        executionCost: portalQuote.estimatedCost,
      };

      setState(prev => ({ ...prev, quote, isQuoting: false }));
      return quote;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to get quote';
      console.error('[WormholeBridge] Quote error:', err);
      setState(prev => ({ ...prev, error: message, isQuoting: false }));
      return null;
    }
  }, []);

  // Execute the bridge transfer
  // Note: requestNativeGas parameter is kept for API compatibility but not used
  const bridge = useCallback(async (
    sourceChain: ChainConfig,
    destChain: ChainConfig,
    amountIn: bigint,
    signer: Signer,
    recipientAddress: string,
    _requestNativeGas: boolean = false,
  ): Promise<BridgeTransfer | null> => {
    setState(prev => ({ ...prev, isBridging: true, error: null }));

    const transferId = `bridge-${Date.now()}`;
    const transfer: BridgeTransfer = {
      id: transferId,
      sourceChain,
      destChain,
      amount: amountIn,
      status: 'pending',
      createdAt: Date.now(),
    };

    setState(prev => ({ ...prev, pendingTransfer: transfer }));
    localStorage.setItem(BRIDGE_PENDING_TX_KEY, serializeTransfer(transfer));

    try {
      // Format amount for the library (expects string with decimals)
      const amountFormatted = formatUnits(amountIn, WELEPHANT_DECIMALS);

      console.log('[WormholeBridge] Initiating bridge:', {
        amount: amountFormatted,
        recipient: recipientAddress,
        sourceChain: sourceChain.name,
        destChain: destChain.name,
      });

      // Execute the bridge using the Portal Bridge library
      const receipt = await bridgeWelephant({
        signer,
        recipient: recipientAddress,
        amount: amountFormatted,
      });

      console.log('[WormholeBridge] Bridge transaction completed:', receipt);

      const txHash = receipt?.hash;

      transfer.txHash = txHash;
      transfer.status = 'submitted';

      setState(prev => ({
        ...prev,
        pendingTransfer: transfer,
        isBridging: false,
      }));
      localStorage.setItem(BRIDGE_PENDING_TX_KEY, serializeTransfer(transfer));

      // Update status to relaying after a short delay
      setTimeout(() => {
        transfer.status = 'relaying';
        setState(prev => ({ ...prev, pendingTransfer: { ...transfer } }));
        localStorage.setItem(BRIDGE_PENDING_TX_KEY, serializeTransfer(transfer));
      }, 2000);

      return transfer;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Bridge failed';
      console.error('[WormholeBridge] Bridge error:', err);

      transfer.status = 'failed';
      transfer.error = message;

      setState(prev => ({
        ...prev,
        pendingTransfer: transfer,
        error: message,
        isBridging: false,
      }));
      localStorage.setItem(BRIDGE_PENDING_TX_KEY, serializeTransfer(transfer));

      return transfer;
    }
  }, []);

  // Clear pending transfer
  const clearPendingTransfer = useCallback(() => {
    localStorage.removeItem(BRIDGE_PENDING_TX_KEY);
    setState(prev => ({ ...prev, pendingTransfer: null }));
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  return {
    ...state,
    getQuote,
    bridge,
    clearPendingTransfer,
    clearError,
  };
}
